/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/date/GetWeekOfYear.ts
 * @create:      2025-07-01 13:21:43.659
 * @modify:      2025-07-11 21:19:12.900
 * @version:     6.0.0
 * @times:       6
 * @lines:       70
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Calculates the week number of the year for a given date.
 *
 * @param date - The date for which to determine the week number.
 * @param iso - Optional. If `true`, calculates the ISO 8601 week number (weeks start on Monday, week 1 is the week with the first Thursday of the year).
 *              If `false` or omitted, calculates the week number based on the local calendar (weeks start on Sunday).
 * @returns The week number of the year for the given date.
 *
 * @remarks
 * - When `iso` is `true`, the calculation follows the ISO 8601 standard.
 * - When `iso` is `false` or not provided, the calculation is based on the local calendar and may differ from ISO week numbers.
 * 
 * @since 2.0.14
 * @version 2021-08-06
 */
export function GetWeekOfYear(date: Date, iso?: boolean) {
    // https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php
    if (iso) {
        // Copy date so don't modify original
        date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
        // Get first day of year
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        // Calculate full weeks to nearest Thursday
        const week = Math.ceil((((date.valueOf() - yearStart.valueOf()) / 86400000) + 1) / 7);

        return week;
    } else {
        const date = new Date();
        const date2 = new Date(date.getFullYear(), 0, 1);
        const week = Math.ceil((((date.getTime() - date2.getTime()) / 86400000) + date2.getDay() + 1) / 7);
        
        return week;
    }
}
